import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronUp, ChevronDown, LogIn } from 'lucide-react';
import useBottomSheetSwipe from '../../hooks/useBottomSheetSwipe';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getBackdropStyle, getHandleStyle, getInnerCardSurface } from './sheetStyle';
import { SheetActionHeader, SheetButton } from './SheetUI';

// Swipe-to-step: drag prstem po elementu, každých `threshold` px = 1 step.
// Up = inkrement, down = dekrement. Pointer capture aby se gesture
// neztratilo když prst opustí element.
function useSwipeStep(onIncrement, onDecrement, threshold = 28) {
    const startY = useRef(null);
    const accumulated = useRef(0);

    const onPointerDown = useCallback((e) => {
        startY.current = e.clientY;
        accumulated.current = 0;
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) { /* noop */ }
    }, []);

    const onPointerMove = useCallback((e) => {
        if (startY.current === null) return;
        const delta = startY.current - e.clientY;
        const stepped = Math.trunc(delta / threshold);
        const diff = stepped - accumulated.current;
        if (diff === 0) return;
        if (diff > 0) for (let i = 0; i < diff; i++) onIncrement();
        else for (let i = 0; i < -diff; i++) onDecrement();
        accumulated.current = stepped;
    }, [onIncrement, onDecrement, threshold]);

    const onPointerEnd = useCallback((e) => {
        startY.current = null;
        accumulated.current = 0;
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) { /* noop */ }
    }, []);

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp: onPointerEnd,
        onPointerCancel: onPointerEnd,
    };
}

// Zaokrouhli HH:MM na nejbližší půl hodinu nahoru (matches backend roundUpToHalfHour).
function roundUpToHalf(hh, mm) {
    if (mm === 0) return { h: hh, m: 0 };
    if (mm <= 30) return { h: hh, m: 30 };
    return { h: (hh + 1) % 24, m: 0 };
}

/**
 * Modal pro zahájení směny s vlastním (manuálním) časem clock-inu.
 *
 * Vlastní time picker (žádný `<input type="time">`) — rendering je identický
 * napříč zařízeními (iOS/Android/desktop) a tappable plochy jsou velké.
 * Hodina ±1, minuta ±5 (clock-in v 5-min granularitě stačí). Auto-time
 * se vždy paralelně ukládá pro audit, manuál nejde retroaktivně fejkovat.
 *
 * Props:
 *   - autoTime: HH:MM (zaokrouhlený automatický návrh, default value)
 *   - onClose: zavřít bez akce
 *   - onConfirm(manualTime): potvrdit s vybraným časem (parent volá clockIn)
 *   - busy: bool, parent může indikovat probíhající clockIn
 */
export default function ClockInManualModal({
    autoTime = '', onClose, onConfirm, busy = false, isDark, isMobile,
    title = 'Vlastní čas zahájení',
    accent = '#FF8A00',
    confirmLabelPrefix = 'Zahájit v',
    busyLabel = 'Zahajuji…',
}) {
    const { dragHandleProps, modalStyle: swipeStyle } = useBottomSheetSwipe(onClose, { enabled: !!isMobile });

    const initFromAutoTime = (s) => {
        const [hh, mm] = (s || '').split(':').map(Number);
        const h = Number.isFinite(hh) ? Math.max(0, Math.min(23, hh)) : 11;
        const m = Number.isFinite(mm) ? Math.max(0, Math.min(59, mm)) : 0;
        return roundUpToHalf(h, m);
    };

    const init = initFromAutoTime(autoTime);
    const [h, setH] = useState(init.h);
    const [m, setM] = useState(init.m);

    useEffect(() => {
        if (!autoTime) return;
        const next = initFromAutoTime(autoTime);
        setH(next.h);
        setM(next.m);
        // jen při změně autoTime (typicky 1× po načtení)
    }, [autoTime]);

    const glass = getGlassSurface(isDark);
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);
    const slotBg = inner.background;
    const slotBorder = inner.border.split(' ').pop();
    const labelMuted = colors.labelMuted;
    const textMuted = colors.textMuted;
    const textPrimary = colors.textPrimary;

    const incH = useCallback(() => setH(prev => (prev + 1) % 24), []);
    const decH = useCallback(() => setH(prev => (prev + 23) % 24), []);
    // Minuta jen :00 nebo :30 — swipe up nastaví 30, down nastaví 0 (bez wrapu,
    // aby vícenásobný swipe neoscilloval mezi hodnotami).
    const incM = useCallback(() => setM(30), []);
    const decM = useCallback(() => setM(0), []);

    const hSwipe = useSwipeStep(incH, decH);
    const mSwipe = useSwipeStep(incM, decM);

    const pad = (n) => String(n).padStart(2, '0');
    const time = `${pad(h)}:${pad(m)}`;
    const canConfirm = !busy;

    // Portal do body — uniká stacking-context trapu.
    return createPortal((
        <div onClick={onClose} style={{
            ...getBackdropStyle(),
            zIndex: 1100,                              // nad ostatními sheety, ClockIn může otevřít z jiného sheetu
            alignItems: 'center', justifyContent: 'flex-end',
            padding: 0,
        }}>
            <div onClick={e => e.stopPropagation()} className="ms-sheet-anim" style={{
                width: '100%', maxWidth: isMobile ? '100%' : 420,
                minHeight: isMobile ? '50vh' : 'auto',
                maxHeight: isMobile ? '85vh' : '90vh',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                borderRadius: `${SHEET_TOKENS.radius.container}px ${SHEET_TOKENS.radius.container}px 0 0`,
                ...glass,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                ...swipeStyle,
            }}>
                {isMobile && (
                    <div {...dragHandleProps} style={{
                        display: 'flex', justifyContent: 'center',
                        paddingTop: 10, paddingBottom: 6,
                        cursor: 'grab', flexShrink: 0,
                    }}>
                        <span style={getHandleStyle(isDark)} />
                    </div>
                )}

                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            color: accent,
                            fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                        }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: accent, boxShadow: `0 0 8px ${accent}80`,
                            }} />
                            {title}
                        </span>
                        <button type="button" onClick={onClose} style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: textMuted, padding: 0,
                        }}>
                            <X size={20} />
                        </button>
                    </div>

                    {autoTime && (
                        <div style={{
                            fontSize: 12, color: labelMuted, lineHeight: 1.4, textAlign: 'center',
                            marginTop: -4,
                        }}>
                            Automatický čas <span style={{
                                color: textPrimary, fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                            }}>{autoTime}</span> se vždy uloží pro audit.
                        </div>
                    )}

                    {/* Spinner — hodina : minuta. Klepni šipky nebo swipuj prstem
                        po číslici nahoru/dolů. */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <Spinner
                            label="Hodina"
                            value={pad(h)}
                            onUp={incH} onDown={decH}
                            swipe={hSwipe}
                            slotBg={slotBg} slotBorder={slotBorder}
                            textPrimary={textPrimary} labelMuted={labelMuted}
                        />
                        <span style={{
                            fontSize: 36, fontWeight: 800, color: textPrimary,
                            fontVariantNumeric: 'tabular-nums', lineHeight: 1, paddingBottom: 22,
                        }}>:</span>
                        <Spinner
                            label="Minuta"
                            value={pad(m)}
                            onUp={incM} onDown={decM}
                            swipe={mSwipe}
                            slotBg={slotBg} slotBorder={slotBorder}
                            textPrimary={textPrimary} labelMuted={labelMuted}
                        />
                    </div>
                    <div style={{
                        textAlign: 'center', fontSize: 11, color: labelMuted, marginTop: -8,
                    }}>
                        Klepni šipky nebo swipuj prstem
                    </div>

                    {/* Akce */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <SheetButton
                            isDark={isDark}
                            variant="secondary"
                            disabled={busy}
                            onClick={onClose}
                            fullWidth={false}
                            style={{ flex: 1 }}
                        >Zrušit</SheetButton>
                        <SheetButton
                            isDark={isDark}
                            variant="primary"
                            accent={accent}
                            disabled={!canConfirm}
                            busy={busy}
                            busyLabel={busyLabel}
                            onClick={() => onConfirm?.(time)}
                            icon={<LogIn size={16} />}
                            fullWidth={false}
                            style={{ flex: 2 }}
                        >{`${confirmLabelPrefix} ${time}`}</SheetButton>
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}

function Spinner({ label, value, onUp, onDown, swipe, slotBg, slotBorder, textPrimary, labelMuted }) {
    const btnStyle = {
        width: 56, height: 44, borderRadius: 7,
        background: 'rgba(0,0,0,0.30)', color: 'rgba(255,255,255,0.85)',
        border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button type="button" onClick={onUp} style={btnStyle} aria-label={`${label} +`}>
                <ChevronUp size={22} />
            </button>
            <div
                {...(swipe || {})}
                style={{
                    width: 96, padding: '14px 0', borderRadius: 9,
                    background: slotBg, border: `1px solid ${slotBorder}`,
                    fontSize: 44, fontWeight: 800, color: textPrimary,
                    fontVariantNumeric: 'tabular-nums', textAlign: 'center', lineHeight: 1,
                    letterSpacing: -1,
                    cursor: swipe ? 'ns-resize' : 'default',
                    touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
                }}>{value}</div>
            <button type="button" onClick={onDown} style={btnStyle} aria-label={`${label} −`}>
                <ChevronDown size={22} />
            </button>
            <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                color: labelMuted, marginTop: 2,
            }}>{label}</span>
        </div>
    );
}
